import React, { useState, useRef } from 'react';
import './ResumeSubPage.css';
import { getStoredToken } from '../../services/authService';
import { fetchProfileResumeAsFile } from '../../services/profileService';
import { evaluateResume, exportJdEvaluationToPdf, MAX_FILE_SIZE_MB } from '../../services/resumeService';
import EvaluationUsageView from '../../components/Resume/EvaluationUsageView';
import ResumeProfileResumeSource from './ResumeProfileResumeSource';

const ACCEPT = '.pdf,.docx';
const MAX_BYTES = 1024 * 1024; // 1MB

const EVALUATE_TYPE = 'resume-screening-fulltime-evaluate';

const statusClass = (status) => {
  if (!status) return '';
  const s = status.toUpperCase();
  if (s === 'PASS') return 'screening-status--pass';
  if (s === 'NEEDS FIX' || s === 'FLAGGED') return 'screening-status--warn';
  if (s === 'FAIL') return 'screening-status--fail';
  if (s === 'INFO') return 'screening-status--info';
  return '';
};

function ScreeningResultView({ data }) {
  const pick = (camel, snake) => (data?.[camel] != null ? data[camel] : data?.[snake]);

  const rec = pick('overallRecommendation', 'overall_recommendation') || '';
  const recClass =
    rec === 'APPROVE' || rec === 'STRONG'
      ? 'screening-recommendation--approve'
      : rec === 'NEEDS REVISION' || rec === 'COMPETITIVE'
        ? 'screening-recommendation--revision'
        : rec === 'REJECT' || rec === 'NOT READY'
          ? 'screening-recommendation--reject'
          : rec === 'NEEDS WORK'
            ? 'screening-recommendation--revision'
            : '';

  const recruiterSignal = pick('recruiterSignal', 'recruiter_signal');
  const signalMaximization = pick('signalMaximization', 'signal_maximization');
  const highestRoiImprovements = pick('highestRoiImprovements', 'highest_roi_improvements');

  const overallSummary = pick('overallSummary', 'overall_summary');
  const structuralVsPackaging = pick('structuralVsPackaging', 'structural_vs_packaging');
  const strongestEngineeringIdentity = pick('strongestEngineeringIdentity', 'strongest_engineering_identity');
  const estimatedLevel = pick('estimatedLevel', 'estimated_level');
  const recommendedApplicationLevel = pick('recommendedApplicationLevel', 'recommended_application_level');
  const estimatedTier = pick('estimatedTier', 'estimated_tier');
  const yearsOfExperience = pick('yearsOfExperience', 'years_of_experience');
  const primaryArchetype = pick('primaryArchetype', 'primary_archetype');
  const secondaryArchetype = pick('secondaryArchetype', 'secondary_archetype');

  return (
    <div className="screening-result">
      <div className="screening-result__header">
        {data.candidate && (
          <p className="screening-result__candidate">Candidate: <strong>{data.candidate}</strong></p>
        )}
        {rec && (
          <p className="screening-result__recommendation">
            Overall: <span className={`screening-recommendation ${recClass}`}>{rec}</span>
          </p>
        )}
      </div>
      {data.sections && data.sections.length > 0 && (
        <div className="screening-result__sections">
          {(overallSummary || structuralVsPackaging || strongestEngineeringIdentity || estimatedTier || yearsOfExperience) && (
            <div className="screening-section">
              <div className="screening-section__head">
                <span className="screening-section__title">Summary & level</span>
              </div>
              <div style={{ fontSize: '0.9rem', lineHeight: 1.5, color: '#374151' }}>
                {primaryArchetype || secondaryArchetype ? (
                  <p style={{ margin: '0 0 0.5rem 0' }}>
                    Archetypes:{' '}
                    <strong>
                      {[primaryArchetype, secondaryArchetype].filter(Boolean).join(' + ') || 'N/A'}
                    </strong>
                  </p>
                ) : null}
                {strongestEngineeringIdentity ? (
                  <p style={{ margin: '0 0 0.5rem 0' }}>
                    Strongest engineering identity: <strong>{strongestEngineeringIdentity}</strong>
                  </p>
                ) : null}
                {yearsOfExperience != null ? (
                  <p style={{ margin: '0 0 0.5rem 0' }}>
                    Years of experience: <strong>{yearsOfExperience}</strong>
                  </p>
                ) : null}
                {estimatedLevel || recommendedApplicationLevel || estimatedTier ? (
                  <p style={{ margin: '0 0 0.5rem 0' }}>
                    Estimated level:{' '}
                    <strong>
                      {[estimatedLevel, recommendedApplicationLevel, estimatedTier].filter(Boolean).join(' / ') || 'N/A'}
                    </strong>
                  </p>
                ) : null}
                {structuralVsPackaging ? (
                  <p style={{ margin: '0 0 0.5rem 0' }}>
                    Structural vs packaging: <strong>{structuralVsPackaging}</strong>
                  </p>
                ) : null}
                {overallSummary ? (
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{overallSummary}</p>
                ) : null}
              </div>
            </div>
          )}

          {recruiterSignal ? (
            <div className="screening-section">
              <div className="screening-section__head">
                <span className="screening-section__title">Recruiter signal</span>
              </div>

              {(() => {
                const t = recruiterSignal.tenureStability || recruiterSignal.tenure_stability;
                const c = recruiterSignal.companyBrand || recruiterSignal.company_brand;
                const car = recruiterSignal.careerTrajectory || recruiterSignal.career_trajectory;
                const r = recruiterSignal.recruiterMemoryTest || recruiterSignal.recruiter_memory_test;
                const o = recruiterSignal.overallMarketSignal || recruiterSignal.overall_market_signal;

                const cats = [
                  { label: 'Tenure stability', val: t },
                  { label: 'Company brand tier', val: c },
                  { label: 'Career trajectory', val: car },
                  { label: 'Recruiter memory test', val: r },
                  { label: 'Overall market signal', val: o },
                ].filter((x) => x.val);

                if (cats.length === 0) return null;

                return (
                  <div className="recruiter-signal">
                    {cats.map((cat, idx) => (
                      <div key={idx} className="recruiter-signal__flag">
                        <div className="recruiter-signal__flag-head">
                          <span className="recruiter-signal__dimension">{cat.label}</span>
                          {cat.val.signal ? (
                            <span
                              className={`recruiter-signal__pill recruiter-signal__pill--${
                                (cat.val.signal || '').toLowerCase() || 'green'
                              }`}
                            >
                              {cat.val.signal}
                            </span>
                          ) : null}
                        </div>
                        {cat.val.assessment ? (
                          <div className="recruiter-signal__assessment">{cat.val.assessment}</div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          ) : null}

          {signalMaximization ? (
            <div className="screening-section">
              <div className="screening-section__head">
                <span className="screening-section__title">Signal maximization</span>
              </div>

              {(() => {
                const currentIdentityStrength =
                  signalMaximization.currentIdentityStrength || signalMaximization.current_identity_strength;
                const strongestSupportingBullets =
                  signalMaximization.strongestSupportingBullets || signalMaximization.strongest_supporting_bullets;
                const genericOrWeakBullets =
                  signalMaximization.genericOrWeakBullets || signalMaximization.generic_or_weak_bullets;
                const identityStrengtheningRewrites =
                  signalMaximization.identityStrengtheningRewrites || signalMaximization.identity_strengthening_rewrites;
                const overallMemorabilityAssessment =
                  signalMaximization.overallMemorabilityAssessment || signalMaximization.overall_memorability_assessment;

                return (
                  <div style={{ fontSize: '0.9rem', lineHeight: 1.5, color: '#374151' }}>
                    {currentIdentityStrength ? (
                      <p style={{ margin: '0 0 0.5rem 0' }}>
                        Current identity strength: <strong>{currentIdentityStrength}</strong>
                      </p>
                    ) : null}

                    {strongestSupportingBullets?.length ? (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Strongest supporting bullets</div>
                        <ul className="screening-section__issues">
                          {strongestSupportingBullets.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {genericOrWeakBullets?.length ? (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Generic / weak bullets</div>
                        <ul className="screening-section__issues">
                          {genericOrWeakBullets.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {identityStrengtheningRewrites?.length ? (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Rewrite suggestions</div>
                        {identityStrengtheningRewrites.map((rw, i) => (
                          <div key={i} style={{ marginBottom: '0.75rem', padding: '0.6rem', background: '#f1f5f9', borderRadius: 6 }}>
                            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Original</div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{rw.original}</div>
                            <div style={{ fontWeight: 700, margin: '0.5rem 0 0.25rem 0' }}>Rewritten</div>
                            <div style={{ whiteSpace: 'pre-wrap', color: '#1e40af' }}>{rw.rewritten}</div>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {overallMemorabilityAssessment ? (
                      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        Overall memorability assessment: <strong>{overallMemorabilityAssessment}</strong>
                      </p>
                    ) : null}
                  </div>
                );
              })()}
            </div>
          ) : null}

          {highestRoiImprovements?.length ? (
            <div className="screening-section">
              <div className="screening-section__head">
                <span className="screening-section__title">Highest ROI improvements</span>
              </div>
              <ul className="screening-section__issues">
                {highestRoiImprovements.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {data.sections.map((section) => (
            <div key={section.id ?? section.title} className="screening-section">
              <div className="screening-section__head">
                <span className="screening-section__title">
                  {section.id != null && `${section.id}. `}{section.title}
                </span>
                {section.status && (
                  <span className={`screening-section__status screening-status ${statusClass(section.status)}`}>
                    {section.status}
                  </span>
                )}
              </div>
              {section.notes && (
                <p className="screening-section__notes">{section.notes}</p>
              )}
              {section.issues && section.issues.length > 0 && (
                <ul className="screening-section__issues">
                  {section.issues.map((issue, idx) => (
                    <li key={idx} className="screening-issue">
                      <div className="screening-issue__problem">{issue.problem}</div>
                      {issue.suggestion && (
                        <div className="screening-issue__suggestion-row">
                          <span className="screening-issue__suggestion-label">Suggestion:</span>
                          <span className="screening-issue__suggestion">{issue.suggestion}</span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {section.flags && section.flags.length > 0 && (
                <div className="screening-section__flags recruiter-signal">
                  <div className="recruiter-signal__title">Recruiter signal</div>
                  {section.flags.map((flag, idx) => {
                    const isTargets = flag.dimension && /suggested application|application target/i.test(flag.dimension);
                    const isTier = flag.dimension && /company brand|tier/i.test(flag.dimension);
                    const assessment = flag.assessment;
                    let targetGroups = [];
                    if (isTargets && assessment) {
                      const lines = assessment.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
                      for (const line of lines) {
                        const colonIdx = line.indexOf(':');
                        if (colonIdx > 0 && colonIdx < 50) {
                          const label = line.slice(0, colonIdx).trim();
                          const rest = line.slice(colonIdx + 1).trim();
                          const items = rest ? rest.split(/[,;]/).map((s) => s.trim()).filter(Boolean) : [];
                          targetGroups.push({ label, items });
                        } else {
                          const items = line.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
                          if (items.length > 0) targetGroups.push({ label: null, items });
                        }
                      }
                    }
                    return (
                      <div
                        key={idx}
                        className={`recruiter-signal__flag ${isTargets ? 'recruiter-signal__flag--highlight' : ''} ${isTier ? 'recruiter-signal__flag--tier' : ''}`}
                      >
                        <div className="recruiter-signal__flag-head">
                          <span className="recruiter-signal__dimension">{flag.dimension}</span>
                          {flag.signal && (
                            <span className={`recruiter-signal__pill recruiter-signal__pill--${(flag.signal || '').toLowerCase()}`}>
                              {flag.signal}
                            </span>
                          )}
                        </div>
                        {isTargets && targetGroups.length > 0 ? (
                          <div className="recruiter-signal__targets">
                            {targetGroups.map((group, gidx) => (
                              <div key={gidx} className="recruiter-signal__target-group">
                                {group.label && (
                                  <div className="recruiter-signal__target-label">{group.label}</div>
                                )}
                                <ul className="recruiter-signal__target-list">
                                  {group.items.map((company, cidx) => (
                                    <li key={cidx}>{company}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        ) : assessment ? (
                          <div className="recruiter-signal__assessment">{assessment}</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EvaluateFulltimeResume = () => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [screeningResult, setScreeningResult] = useState(null);
  const [evaluationUsage, setEvaluationUsage] = useState(null);
  const [error, setError] = useState(null);
  const [pdfExportLoading, setPdfExportLoading] = useState(false);
  const [resumeSource, setResumeSource] = useState(() =>
    getStoredToken() ? 'pick' : 'upload'
  );
  const [profileResumeLoading, setProfileResumeLoading] = useState(false);
  const inputRef = useRef(null);

  const clearState = () => {
    setError(null);
    setEvaluation(null);
    setScreeningResult(null);
    setEvaluationUsage(null);
    setPdfExportLoading(false);
  };

  const evaluationExportPayload = () => {
    if (screeningResult && typeof screeningResult === 'object') {
      try {
        return JSON.stringify(screeningResult);
      } catch {
        return '';
      }
    }
    if (typeof evaluation === 'string' && evaluation.trim().startsWith('{')) {
      try {
        const o = JSON.parse(evaluation);
        if (o && typeof o === 'object' && (Array.isArray(o.sections) || o.overallRecommendation)) {
          return JSON.stringify(o);
        }
      } catch {
        /* use raw string below */
      }
    }
    return typeof evaluation === 'string' ? evaluation : '';
  };

  const onExportPdf = async () => {
    const payload = evaluationExportPayload();
    if (!payload.trim() || pdfExportLoading) return;
    setPdfExportLoading(true);
    try {
      const { blob, filename } = await exportJdEvaluationToPdf(payload.trim());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'PDF export failed.');
    } finally {
      setPdfExportLoading(false);
    }
  };

  const validateFile = (f) => {
    if (!f) return null;
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    const validExt = /\.(pdf|docx)$/i.test(f.name);
    if (!validExt && !validTypes.includes(f.type)) {
      return 'Please upload a PDF or DOCX file.';
    }
    if (f.size > MAX_BYTES) {
      return `File must be under ${MAX_FILE_SIZE_MB}MB.`;
    }
    return null;
  };

  const handleFile = (selectedFile) => {
    clearState();
    if (getStoredToken()) setResumeSource('upload');
    if (!selectedFile) {
      setFile(null);
      return;
    }
    const err = validateFile(selectedFile);
    if (err) {
      setError(err);
      setFile(null);
      return;
    }
    setFile(selectedFile);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files[0];
    handleFile(f || null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = () => {
    setDragActive(false);
  };

  const onInputChange = (e) => {
    const f = e.target.files[0];
    handleFile(f || null);
    e.target.value = '';
  };

  const onUploadClick = () => {
    inputRef.current?.click();
  };

  const onClearFile = (e) => {
    e.stopPropagation();
    if (loading) return;
    setFile(null);
    if (getStoredToken()) setResumeSource('upload');
    clearState();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPickUploadResume = () => {
    setResumeSource('upload');
  };

  const onBackToResumeChoices = () => {
    setResumeSource('pick');
    setFile(null);
    clearState();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPickProfileResume = async () => {
    setProfileResumeLoading(true);
    setError(null);
    try {
      const f = await fetchProfileResumeAsFile();
      if (!f) {
        setError('No resume in your profile. Add one under Profile.');
        return;
      }
      const err = validateFile(f);
      if (err) {
        setError(err);
        return;
      }
      clearState();
      setFile(f);
      setResumeSource('profile');
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load profile resume.');
    } finally {
      setProfileResumeLoading(false);
    }
  };

  const onChooseFileUploadInstead = () => {
    setResumeSource('upload');
    setFile(null);
    clearState();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onSubmit = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const result = await evaluateResume(file, EVALUATE_TYPE);
      if (result.success) {
        setEvaluation(result.evaluation);
        setScreeningResult(result.screeningResult || null);
        setEvaluationUsage(result.usage || null);
      } else {
        setError(result.error || 'Evaluation failed.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const userLoggedIn = !!getStoredToken();
  const resumeSourceEffective = userLoggedIn ? resumeSource : 'upload';
  const showManualUploadZone = resumeSourceEffective === 'upload';

  return (
    <div className="resume-subpage">
      <h2>Evaluate Fulltime Resume</h2>

      <ResumeProfileResumeSource
        loggedIn={userLoggedIn}
        resumeSource={resumeSourceEffective}
        profileResumeLoading={profileResumeLoading}
        fileName={file?.name}
        disableActions={loading}
        onPickProfileResume={onPickProfileResume}
        onPickUploadResume={onPickUploadResume}
        onChooseFileUploadInstead={onChooseFileUploadInstead}
        onBackToResumeChoices={onBackToResumeChoices}
      />

      {showManualUploadZone && (
        <div
          className={`resume-upload-zone ${dragActive ? 'resume-upload-zone--drag' : ''} ${error ? 'resume-upload-zone--error' : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={onUploadClick}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={onInputChange}
            className="resume-upload-zone__input"
            disabled={loading}
          />
          {file ? (
            <div className="resume-upload-zone__file">
              <span className="resume-upload-zone__file-name">{file.name}</span>
              <span className="resume-upload-zone__file-size">
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
              <button
                type="button"
                className="resume-upload-zone__clear"
                onClick={onClearFile}
                disabled={loading}
                aria-label="Remove file"
                title="Remove and re-upload"
              >
                ×
              </button>
            </div>
          ) : (
            <p className="resume-upload-zone__hint">
              Drop your resume here, or click to browse. Max {MAX_FILE_SIZE_MB}MB · PDF or DOCX
            </p>
          )}
        </div>
      )}

      {error && <div className="resume-subpage__error">{error}</div>}

      <div className="resume-subpage__actions">
        <button
          type="button"
          className="resume-subpage__submit"
          onClick={onSubmit}
          disabled={!file || loading}
        >
          {loading ? 'Evaluating…' : 'Evaluate'}
        </button>
        <button
          type="button"
          className="resume-subpage__submit"
          onClick={onExportPdf}
          disabled={!evaluationExportPayload().trim() || loading || pdfExportLoading}
        >
          {pdfExportLoading ? 'Preparing PDF…' : 'Download Report'}
        </button>
      </div>

      {evaluation && (
        <div className="resume-subpage__result">
          <h3>Evaluation</h3>
          {screeningResult ? (
            <ScreeningResultView data={screeningResult} />
          ) : (
            <div className="resume-subpage__result-content">{evaluation}</div>
          )}
          <EvaluationUsageView usage={evaluationUsage} />
        </div>
      )}
    </div>
  );
};

export default EvaluateFulltimeResume;
